import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { TOURS } from "../tour/tourSteps";

const TourContext = createContext(null);

export function TourProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enabled, setEnabled] = useState(true); // tenant master toggle
  const [tourKey, setTourKey] = useState("universal");
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState(null); // {top,left,width,height} or null
  // Track if user has already seen the auto-start once per device.
  const seenKey = "salonflow_tour_seen_v1";
  const navigatingRef = useRef(false);

  // Load tenant setting whenever auth user changes.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/settings/onboarding-tour");
        if (!cancelled) setEnabled(res.data?.enabled !== false);
      } catch {
        if (!cancelled) setEnabled(true);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const steps = TOURS[tourKey] || TOURS.universal;
  const totalSteps = steps.length;
  const currentStep = active ? steps[stepIndex] : null;

  // Fire per-step custom events (used e.g. by Training Studio to switch
  // tabs before the spotlight targets an in-tab element).
  useEffect(() => {
    if (!active || !currentStep?.dispatchEvent) return;
    const { name, detail } = currentStep.dispatchEvent;
    try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* no-op */ }
  }, [active, stepIndex, currentStep]);

  // Locate the target element & compute its bounding rect. Poll briefly
  // because the target might mount after a route change or a tab switch.
  useEffect(() => {
    if (!active || !currentStep) { setTargetRect(null); return; }
    if (!currentStep.target) { setTargetRect(null); return; } // welcome/intro step

    let cancelled = false;
    let tries = 0;
    const findTarget = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-testid="${currentStep.target}"]`);
      if (el) {
        const r = el.getBoundingClientRect();
        setTargetRect({
          top: r.top + window.scrollY,
          left: r.left + window.scrollX,
          width: r.width,
          height: r.height,
        });
        // Scroll the element into view so the spotlight is visible.
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
      } else {
        setTargetRect(null);
        if (tries < 30) { tries += 1; setTimeout(findTarget, 200); }
      }
    };
    findTarget();
    return () => { cancelled = true; };
  }, [active, stepIndex, currentStep]);

  // Recompute on resize/scroll so the spotlight follows.
  useEffect(() => {
    if (!active || !currentStep?.target) return;
    const recompute = () => {
      const el = document.querySelector(`[data-testid="${currentStep.target}"]`);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setTargetRect({
        top: r.top + window.scrollY,
        left: r.left + window.scrollX,
        width: r.width,
        height: r.height,
      });
    };
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [active, currentStep]);

  const goTo = useCallback((idx) => {
    if (idx < 0 || idx >= totalSteps) return;
    const next = steps[idx];
    setStepIndex(idx);
    if (next?.route && next.route !== window.location.pathname) {
      navigatingRef.current = true;
      navigate(next.route);
      setTimeout(() => { navigatingRef.current = false; }, 600);
    }
  }, [navigate, totalSteps, steps]);

  const start = useCallback((key = "universal") => {
    if (!enabled) return;
    const list = TOURS[key] || TOURS.universal;
    setTourKey(key);
    setStepIndex(0);
    setActive(true);
    const first = list[0];
    if (first?.route && first.route !== window.location.pathname) {
      navigate(first.route);
    }
  }, [enabled, navigate]);

  const next = useCallback(() => {
    if (stepIndex >= totalSteps - 1) {
      setActive(false);
      try { localStorage.setItem(seenKey, "1"); } catch { /* no-op */ }
      return;
    }
    goTo(stepIndex + 1);
  }, [stepIndex, totalSteps, goTo]);

  const prev = useCallback(() => { if (stepIndex > 0) goTo(stepIndex - 1); }, [stepIndex, goTo]);

  const end = useCallback(() => {
    setActive(false);
    try { localStorage.setItem(seenKey, "1"); } catch { /* no-op */ }
  }, []);

  return (
    <TourContext.Provider value={{
      enabled, setEnabled, active, stepIndex, totalSteps, currentStep, tourKey,
      targetRect, start, next, prev, end, goTo,
    }}>
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
}
